import { Injectable, inject } from '@angular/core';
import { Observable, of, throwError, Subject } from 'rxjs';
import { shareReplay, map, catchError, tap, startWith, switchMap } from 'rxjs/operators';
import { ProductDetail } from '../../models/product.model';
import { ProductReviewRequest } from '../../api/models/product.dto';
import { BoatMapper } from '../../mappers/boat.mapper';
import { MerchandiseMapper } from '../../mappers/merchandise.mapper';
// removed import
import { DEFAULT_PAGE_SIZE } from '../../constants/pagination.constants';
import { environment } from '../../../../environments/environment';
import { HttpClient } from '@angular/common/http';

const NO_IMAGE = '/assets/no-image.png';

/**
 * Public listing service — reads GET /api/Product exclusively.
 *
 * KEY DESIGN DECISIONS (based on confirmed API response shape):
 *  - productTypeId scalar may be NULL — use productType.productTypeName navigation instead
 *  - productBoats[] contains the linked boat data under productBoats[].boat
 *  - boatPhotos is the photo array: boatPhotos[].photo.photoUrl or boatPhotos[].photoUrl
 *  - Public sale statuses match the API: Available and Sale Pending
 *  - brokerId null products are shown (backend JWT not yet setting brokerId)
 */
@Injectable({ providedIn: 'root' })
export class PublicListingService {
  private http = inject(HttpClient);
  // private productService = inject(ProductService);
  // private appMainService = inject(AppMainService);
  private readonly productApi = `${environment.apiUrl}/Product`;

  private cachedYachts$?: Observable<ProductDetail[]>;
  private cachedMerchs$?: Observable<ProductDetail[]>;

  private readonly listingChanges$ = new Subject<void>();

  invalidateCache() {
    this.listingChanges$.next();
  }

  // ── URL Cleaning ────────────────────────────────────────

  /**
   * Extracts clean Azure blob URL from a potentially broken photoUrl.
   * Broken pattern: "https://api-site/{\"url\":\"https://blob-url\"}"
   * This regex finds the nested blob URL and returns it.
   */
  private cleanPhotoUrl(url: string | null | undefined): string {
    if (!url) return '';
    // Try to extract embedded blob URL (handles the broken photoUrl pattern)
    const blobMatch = url.match(/https:\/\/\S+\.blob\.core\.windows\.net\/[^\s"\\}]+/);
    if (blobMatch) return blobMatch[0];
    return url;
  }

  // ── Product → ProductDetail Mapping ─────────────────────

  private toProductDetail(dto: any, forceMerch: boolean = false): ProductDetail {
    const typeName = dto.productType?.productTypeName?.toLowerCase() || '';
    const isBoat = (typeName.includes('boat') || !!dto.boat) && !forceMerch;
    const isMerch =
      forceMerch ||
      typeName.includes('merchandise') ||
      typeName.includes('apparel') ||
      typeName.includes('acc') ||
      !!dto.merchandise ||
      !!dto.merchandiseId;

    if (isBoat) {
      const bDto = dto.boat;
      if (!bDto) {
        console.error(
          `[PublicListingService] Data Corruption: Yacht product ${dto.productId} missing nested boat object. Normalizing to safe model.`,
          dto,
        );
        return {
          id: dto.productId?.toString() || '',
          type: 'boat',
          name: dto.productName,
          offerName: dto.productName || '',
          description: dto.productDescription || '',
          price: dto.price ?? null,
          status: dto.status?.statusName ?? 'Available',
          brokerId: dto.brokerId || undefined,
          isActive: dto.isActive !== false,
          stockCount: 1,
          photos: [],
          mainPhoto: NO_IMAGE,
          details: { type: 'Yacht', year: null, hullMaterial: '—' },
          averageRating: 0,
          reviewCount: 0,
          boatThumbnailPhoto: dto.boatThumbnailPhoto,
          shareUrl: '', // Legacy property — now handled by UrlService
        } as any;
      }

      const boatProduct = BoatMapper.toBoatProduct(bDto);
      const reviews = dto.productReviews || [];
      const averageRating =
        reviews.length > 0
          ? reviews.reduce((acc: number, r: any) => acc + (r.rating || 0), 0) / reviews.length
          : 0;

      return {
        ...boatProduct,
        listingReference: dto.listingReference,
        id: dto.productId?.toString() || boatProduct.id,
        price: dto.price ?? boatProduct.price ?? null,
        name: dto.productName || boatProduct.name || 'Unnamed Yacht',
        offerName: dto.productName || '',
        description: dto.productDescription || boatProduct.description || '',
        status: dto.status?.statusName?.trim() || boatProduct.status || 'Available',
        brokerId: dto.brokerId || boatProduct.brokerId,
        isActive: dto.isActive !== false,
        averageRating,
        reviewCount: reviews.length,
        shareUrl: '', // Legacy property
      };
    }

    if (isMerch) {
      const reviews = dto.productReviews || [];
      const averageRating =
        reviews.length > 0
          ? reviews.reduce((acc: number, r: any) => acc + (r.rating || 0), 0) / reviews.length
          : 0;

      return {
        id: dto.productId?.toString() || '',
        type: 'merchandise',
        name: dto.merchandise?.merchandiseName || dto.productName || 'Unnamed Item',
        offerName: dto.productName || dto.merchandise?.merchandiseName || '',
        description: dto.merchandise?.merchandiseDescription || dto.productDescription || '',
        price: dto.merchandise?.price || dto.price || null,
        status:
          dto.status?.statusName?.trim() || dto.merchandise?.status?.statusName || 'Available',
        brokerId: dto.brokerId || undefined,
        isActive: dto.isActive !== false,
        stockCount: dto.merchandise?.merchandiseCount || 0,
        mainPhoto:
          dto.merchandiseThumbnailPhoto?.photoUrl ||
          dto.merchandise?.thumbnailPhoto?.photoUrl ||
          dto.boatThumbnailPhoto?.photoUrl ||
          NO_IMAGE,
        photos: [],
        averageRating,
        reviewCount: reviews.length,
        contacts: dto.productContacts || [],
        shareUrl: '', // Legacy property
      } as any;
    }

    // Default fallback if unknown type
    const fallbackType = isMerch ? 'merchandise' : isBoat ? 'boat' : 'product';
    return {
      id: dto.productId?.toString() || '',
      type: fallbackType,
      name: dto.productName || 'Unnamed Product',
      offerName: dto.productName || '',
      description: dto.productDescription || '',
      price: dto.price ?? null,
      status: dto.status?.statusName ?? 'Available',
      brokerId: dto.brokerId || undefined,
      isActive: dto.isActive !== false,
      averageRating: 0,
      reviewCount: 0,
      photos: [],
      mainPhoto: NO_IMAGE,
      boatThumbnailPhoto: dto.boatThumbnailPhoto,
      merchandiseThumbnailPhoto: dto.merchandiseThumbnailPhoto,
      shareUrl: '',
    } as any;
  }

  // ── Filter helpers ───────────────────────────────────────

  private isBoatProduct(dto: any): boolean {
    // Use productType navigation (since scalar productTypeId is often null in response)
    const typeName = dto.productType?.productTypeName?.toLowerCase() ?? '';
    if (typeName.includes('boat')) return true;

    // Fallback: has productBoats with data or direct boat property
    if (dto.productBoats?.length > 0 || dto.boat) return true;

    return false;
  }

  private isMerchProduct(dto: any): boolean {
    const typeName = dto.productType?.productTypeName?.toLowerCase() ?? '';
    if (typeName.includes('merch')) return true;
    if (dto.productMerchandises?.length > 0 || dto.merchandise) return true;
    return false;
  }

  private isVisible(dto: any): boolean {
    const status = dto.status?.statusName?.trim().toLowerCase();
    return dto.isActive !== false && (status === 'available' || status === 'sale pending');
  }

  // ── Core fetch ───────────────────────────────────────────

  private fetchAll(endpoint: string): Observable<any[]> {
    const url = `${environment.apiUrl}/Product/${endpoint}`;
    return this.http.get<any[]>(url).pipe(
      catchError((err) => {
        console.error(`[PublicListingService] ${endpoint} fetch failed:`, err);
        return of([]);
      }),
    );
  }

  public getCachedYachts(): Observable<any[]> {
    if (!this.cachedYachts$) {
      this.cachedYachts$ = this.watchSaleListings('sale/boat');
    }
    return this.cachedYachts$;
  }

  public getCachedMerchs(): Observable<any[]> {
    if (!this.cachedMerchs$) {
      this.cachedMerchs$ = this.watchSaleListings('sale/merchandise');
    }
    return this.cachedMerchs$;
  }

  private watchSaleListings(endpoint: string): Observable<any[]> {
    return this.listingChanges$.pipe(
      startWith(undefined),
      switchMap(() => this.fetchAll(endpoint)),
      map((products) => products.filter((product) => this.isVisible(product))),
      // Share a page's request, but refetch when a visitor returns to the public page.
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  // ── Public API ─────────────────────────────────────────

  searchYachts(
    page: number = 1,
    size: number = DEFAULT_PAGE_SIZE,
    sort: string = 'newest',
    filters: any = {},
  ): Observable<{ items: any[]; totalItems: number }> {
    return this.getCachedYachts().pipe(
      map((boats) => {
        // Source Validation: totalItems must reflect API response length for correct pagination
        const apiTotal = boats.length;
        let filtered = [...boats];

        // Apply filters with Zero-Loss safety
        if (filters.minPrice)
          filtered = filtered.filter((b) => b.price === null || b.price >= filters.minPrice);
        if (filters.maxPrice)
          filtered = filtered.filter((b) => b.price === null || b.price <= filters.maxPrice);

        if (filters.minYear) {
          filtered = filtered.filter((b) => {
            const year = b.boat?.boatYear ?? b.boatYear ?? null;
            return year === null || year >= filters.minYear;
          });
        }
        if (filters.maxYear) {
          filtered = filtered.filter((b) => {
            const year = b.boat?.boatYear ?? b.boatYear ?? null;
            return year === null || year <= filters.maxYear;
          });
        }

        if (filters.condition) {
          filtered = filtered.filter((b) => {
            const cond = (b.boat?.condition || b.condition || '').trim().toLowerCase();
            return cond === filters.condition.trim().toLowerCase();
          });
        }

        if (filters.locationId) {
          filtered = filtered.filter((b) => {
            // Check both direct locationId (if flat) and productLocations array
            const directId = (b as any).locationId;
            const hasLocation = (b.productLocations || []).some(
              (l: any) => l.locationId === +filters.locationId,
            );
            return directId === +filters.locationId || hasLocation;
          });
        }

        if (filters.minRating) {
          filtered = filtered.filter((b) => {
            const reviews = b.productReviews || [];
            const avg =
              reviews.length > 0
                ? reviews.reduce((acc: number, r: any) => acc + (r.rating || 0), 0) / reviews.length
                : 0;
            return avg >= +filters.minRating;
          });
        }

        filtered.sort((a, b) => {
          if (sort === 'price-low') return (a.price || 0) - (b.price || 0);
          if (sort === 'price-high') return (b.price || 0) - (a.price || 0);
          return (b.productId || 0) - (a.productId || 0);
        });

        // Current count after active user filters
        const filteredTotal = filtered.length;

        // Paginate
        const mappedItems = filtered
          .slice((page - 1) * size, page * size)
          .map((dto) => (this as any).toProductDetail(dto));

        console.log('[PublicListingService] Yachts Search Count:', {
          api_raw: apiTotal,
          filtered: filteredTotal,
          mapped: mappedItems.length,
        });

        // Use filtered length for pagination to avoid empty pages,
        // but ensure filtered logic doesn't drop nulls.
        return { items: mappedItems, totalItems: filteredTotal };
      }),
    );
  }

  searchMerchandises(
    page: number = 1,
    size: number = DEFAULT_PAGE_SIZE,
    sort: string = 'newest',
    filters: any = {},
  ): Observable<{ items: any[]; totalItems: number }> {
    return this.getCachedMerchs().pipe(
      map((merchs) => {
        const apiTotal = merchs.length;
        let filtered = [...merchs];

        if (filters.minPrice)
          filtered = filtered.filter((m) => m.price === null || m.price >= filters.minPrice);
        if (filters.maxPrice)
          filtered = filtered.filter((m) => m.price === null || m.price <= filters.maxPrice);

        if (filters.featureIds && Array.isArray(filters.featureIds)) {
          filtered = filtered.filter((m) => {
            const mappings = m.merchandise?.merchandiseFeatureMappings || [];
            return mappings.some((fm: any) => filters.featureIds.includes(fm.merchandiseFeatureId));
          });
        }

        if (filters.minRating) {
          filtered = filtered.filter((m) => {
            const reviews = m.productReviews || [];
            const avg =
              reviews.length > 0
                ? reviews.reduce((acc: number, r: any) => acc + (r.rating || 0), 0) / reviews.length
                : 0;
            return avg >= +filters.minRating;
          });
        }

        filtered.sort((a, b) => {
          if (sort === 'price-low') return (a.price || 0) - (b.price || 0);
          if (sort === 'price-high') return (b.price || 0) - (a.price || 0);
          return (b.productId || 0) - (a.productId || 0);
        });

        const totalItems = filtered.length;
        const mappedItems = filtered
          .slice((page - 1) * size, page * size)
          .map((dto) => (this as any).toProductDetail(dto, true));

        console.log('[PublicListingService] Merch Search Debug:', {
          raw_api_count: apiTotal,
          filtered_count: totalItems,
          mapped_count: mappedItems.length,
          first_item_id: mappedItems[0]?.id,
        });

        return { items: mappedItems, totalItems };
      }),
    );
  }

  getBoatById(id: number): Observable<any> {
    return this.http.get<any>(`${environment.apiUrl}/Boat/${id}`).pipe(
      map((dto) => {
        if (!dto) return null;
        // Map to standard ProductDetail to ensure all components see same shape
        const boatProduct = BoatMapper.toBoatProduct(dto);

        const reviews = dto.productReviews || [];
        const averageRating =
          reviews.length > 0
            ? reviews.reduce((acc: number, r: any) => acc + (r.rating || 0), 0) / reviews.length
            : 0;

        return {
          ...boatProduct,
          reviews,
          averageRating,
          reviewCount: reviews.length,
          // Ensure nested objects are available for components that drill down
          raw: dto,
        };
      }),
      catchError(() => of(null)),
    );
  }

  getMerchandiseById(id: number): Observable<any> {
    return this.http.get<any>(`${environment.apiUrl}/Merchandise/${id}`).pipe(
      map((dto) => {
        if (!dto) return null;
        const merchProduct = MerchandiseMapper.toMerchandiseProduct(dto);

        const reviews = dto.productReviews || [];
        const averageRating =
          reviews.length > 0
            ? reviews.reduce((acc: number, r: any) => acc + (r.rating || 0), 0) / reviews.length
            : 0;

        return {
          ...merchProduct,
          reviews,
          averageRating,
          reviewCount: reviews.length,
          raw: dto,
        };
      }),
      catchError(() => of(null)),
    );
  }

  getBoatProductDetails(productId: number): Observable<any> {
    return this.http.get<any>(`${environment.apiUrl}/Product/${productId}/boat`).pipe(
      catchError((err) => {
        console.error(`[PublicListingService] getBoatProductDetails failed:`, err);
        return of(null);
      }),
    );
  }

  getMerchProductDetails(productId: number): Observable<any> {
    return this.http.get<any>(`${environment.apiUrl}/Product/${productId}/merchandise`).pipe(
      catchError((err) => {
        console.error(`[PublicListingService] getMerchProductDetails failed:`, err);
        return of(null);
      }),
    );
  }

  reviewProduct(productId: number, payload: ProductReviewRequest): Observable<any> {
    return this.http
      .post(`${environment.apiUrl}/Product/review-product/${productId}`, payload)
      .pipe(
        tap(() => this.invalidateCache()),
        catchError((err) => throwError(() => err)),
      );
  }

}
